// Centralized logging utility
// Ensures consistent, structured logs across the app without exposing sensitive data or database IDs

export type LogLevel = "ACTION" | "SUCCESS" | "ERROR" | "TOAST";

const SENSITIVE_SUBSTRINGS = [
  "password",
  "secret",
  "token",
  "key",
  "webhook",
  "signature",
];

const BANNED_KEYS = new Set<string>([
  // common identifiers (snake_case)
  "id",
  "user_id",
  "assessment_id",
  "question_id",
  "notification_id",
  "profile_id",
  "role_id",
  "gateway_id",
  "order_id",
  "transaction_id",
  "payment_id",
  "session_id",
  "customer_id",
  "uuid",
  // camelCase
  "userId",
  "assessmentId",
  "questionId",
  "notificationId",
  "profileId",
  "roleId",
  "gatewayId",
  "orderId",
  "transactionId",
  "paymentId",
  "sessionId",
  "customerId",
]);

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  if (BANNED_KEYS.has(key) || BANNED_KEYS.has(lower)) return true;
  return SENSITIVE_SUBSTRINGS.some((s) => lower.includes(s));
}

function sanitizeData<T = unknown>(input: T, seen = new WeakSet()): any {
  if (input === null || input === undefined) return input;
  if (typeof input !== "object") return input;

  if (seen.has(input as any)) return undefined; // avoid cycles
  seen.add(input as any);

  if (Array.isArray(input)) {
    return input.map((item) => sanitizeData(item, seen));
  }

  const output: Record<string, any> = {};
  for (const [key, value] of Object.entries(input as any)) {
    if (isSensitiveKey(key)) continue;
    output[key] = sanitizeData(value as any, seen);
  }
  return output;
}

export function logAction(level: LogLevel, message: string, data?: any) {
  const ts = new Date().toISOString();
  const prefix = level === "TOAST" ? "[TOAST]" : `[${ts}] [${level}]`;

  const safeData = sanitizeData(data);

  try {
    if (level === "ERROR") {
      if (safeData !== undefined) {
        console.error(`${prefix} ${message}`, safeData);
      } else {
        console.error(`${prefix} ${message}`);
      }
    } else {
      if (safeData !== undefined) {
        console.log(`${prefix} ${message}`, safeData);
      } else {
        console.log(`${prefix} ${message}`);
      }
    }
  } catch {
    // Never let logging break the app
    try {
      console.log(`${prefix} ${message}`);
    } catch {}
  }
}

// Convenience helpers
export const Log = {
  action: (message: string, data?: any) => logAction("ACTION", message, data),
  success: (message: string, data?: any) => logAction("SUCCESS", message, data),
  error: (message: string, data?: any) => logAction("ERROR", message, data),
  toast: (type: "Success" | "Error" | "Info" | "Warning" | string, message: string, data?: any) =>
    logAction("TOAST", `${type}: ${message}`, data),
};
